import { useEffect, useState } from "react";
import {
  Button,
  SearchInput,
  Tag,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Alert,
  AlertDescription,
  AlertTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  InputField,
  Label,
  Textarea,
  RadioGroup,
  RadioGroupItem,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
  DatePicker,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Divider,
} from "@jem-hr/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Eye,
  Settings as SettingsIcon,
  Sun,
  MapPin,
  Clock,
  Pencil,
  AlertCircle,
  Paperclip,
  CheckCircle2,
  AlertTriangle,
  UserX,
  ShieldCheck,
  CircleOff,
  MapPinOff,
  Bell,
  ArrowRight,
  Download,
  Users as UsersIcon,
  UserSearch,
} from "lucide-react";
import { ReportWizardDialog, type ReportScope } from "../components/ReportWizard";
import { Toaster } from "@jem-hr/jem-ui";

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

interface SiteEmployee {
  id: string;
  name: string;
  shift: string;
  shiftTime: string;
  statuses: { label: string; variant: "success" | "failed" | "processing" | "drafted" }[];
  dotColor: "green" | "red" | "amber" | "orange" | "gray" | "none"; // status indicator dot
  clockedIn?: string;
  clockedInBy?: "Supervisor" | "Admin";
  clockedOut?: string;
  clockedOutBy?: "Supervisor" | "Admin";
  actualHours?: string;
  supervisorApproval?: boolean;
  attachment?: string;
  timelinePercent: number; // 0-100, how far through the shift
  replacedBy?: { name: string }; // if this employee was replaced
}

interface RosterGroup {
  id: string;
  code: string; // e.g. "RD016D"
  name: string; // e.g. "SASOL SHIFT SUPERVISORS"
  parentSite: string; // e.g. "JHB CBD"
  onsite: number;
  scheduled: number;
  employees: SiteEmployee[];
}

interface SiteDetail {
  name: string;
  region: string;
  client: string;
  date: string;
  totalOnsite: number;
  totalScheduled: number;
  shiftCoverageHours: string;
  shiftCoveragePercent: number;
  stats: { label: string; count: number; variant: "success" | "failed" | "processing" | "drafted" }[];
  employees: SiteEmployee[]; // legacy single group
  rosterGroups?: RosterGroup[]; // new — multiple roster groups for accordion display
  supervisorRequest?: { name: string };
}

interface SiteRow {
  name: string;
  scheduled: number;
  clockedIn: number;
  noShow: number | string;
  late: number;
  rosterAdherence: number;
  detail?: SiteDetail;
}

interface AreaRow {
  name: string;
  scheduled: number;
  clockedIn: number;
  noShow: number | string;
  late: number;
  rosterAdherence: number;
  sites: SiteRow[];
}

interface RegionRow {
  name: string;
  color: string;
  totalSites: number;
  scheduled: number;
  clockedIn: number;
  noShow: number | string;
  late: number;
  rosterAdherence: number;
  areas: AreaRow[];
}

/* ================================================================== */
/*  Exception Types & Mock Data                                        */
/* ================================================================== */

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDateStr(dateStr: string): string {
  const [y, m, d] = dateStr.split("-");
  return `${parseInt(d, 10)} ${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

function parseDateStr(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

type ExceptionType = "no-show" | "supervisor-clock" | "no-data" | "geofence";

interface Exception {
  id: string;
  type: ExceptionType;
  employeeName: string;
  site: string;
  region: string;
  shift: string;
  shiftTime: string;
  date: string;
  timestamp: string;
  detail: string;
  resolved: boolean;
  /** Geofence-specific: whether this was a clock-in or clock-out event. */
  geofenceDirection?: "in" | "out";
  /** Geofence-specific: distance outside the boundary in metres. */
  geofenceDistanceMeters?: number;
}

/* -- Geofence-violation estimation (ENG-4300) --------------------------
 * Constants live here so QA and engineering see the same numbers as the
 * Linear ticket. Walking-speed benchmark is 80 m/min (~5 km/h, standard
 * pedestrian). 5 km cap is operational: anyone further is in a vehicle,
 * at the wrong site, or has a broken GPS — those cases get manual review,
 * not an estimate. */
const GEOFENCE_WALKING_SPEED_M_PER_MIN = 80;
const GEOFENCE_DISTANCE_CAP_METERS = 5000;

function parseClockTime(s: string): number {
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!m) return 0;
  let h = parseInt(m[1], 10);
  const mins = parseInt(m[2], 10);
  const ap = m[3].toLowerCase();
  if (ap === "pm" && h !== 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  return h * 60 + mins;
}

function formatClockTime(totalMinutes: number): string {
  const wrapped = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const h24 = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  const ap = h24 < 12 ? "am" : "pm";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${m.toString().padStart(2, "0")}${ap}`;
}

function formatGeofenceDistance(m: number): string {
  if (m < 1000) return `${m} m`;
  const km = m / 1000;
  return `${km % 1 === 0 ? km.toFixed(0) : km.toFixed(1)} km`;
}

function computeGeofenceAdjustedTime(
  baseTimestamp: string,
  direction: "in" | "out",
  distanceMeters: number,
): { adjusted: string; deltaMinutes: number } | null {
  if (distanceMeters > GEOFENCE_DISTANCE_CAP_METERS) return null;
  const deltaMinutes = Math.ceil(distanceMeters / GEOFENCE_WALKING_SPEED_M_PER_MIN);
  const baseMinutes = parseClockTime(baseTimestamp);
  const adjustedMinutes =
    direction === "in" ? baseMinutes + deltaMinutes : baseMinutes - deltaMinutes;
  return { adjusted: formatClockTime(adjustedMinutes), deltaMinutes };
}

const exceptionTypeLabels: Record<ExceptionType, string> = {
  "no-show": "No Show",
  "supervisor-clock": "Supervisor Clock-In",
  "no-data": "No Data",
  "geofence": "Geofence Violation",
};

const exceptionTypeIcons: Record<ExceptionType, typeof UserX> = {
  "no-show": UserX,
  "supervisor-clock": ShieldCheck,
  "no-data": CircleOff,
  "geofence": MapPinOff,
};

const exceptionTypeVariants: Record<ExceptionType, "failed" | "processing" | "drafted"> = {
  "no-show": "failed",
  "supervisor-clock": "processing",
  "no-data": "drafted",
  "geofence": "failed",
};

const mockExceptions: Exception[] = [
  // No Shows from Reminder Message
  { id: "ex-1", type: "no-show", employeeName: "Themba Ndlovu", site: "Retail Floor", region: "Western Cape", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-08", timestamp: "6:15am", detail: "No response to reminder message. Shift started 15 min ago.", resolved: false },
  { id: "ex-2", type: "no-show", employeeName: "Jake Smith", site: "Sandton City", region: "Gauteng", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-08", timestamp: "6:32am", detail: "Reminder message read but no clock-in received.", resolved: false },
  { id: "ex-3", type: "no-show", employeeName: "Lisa Pieterse", site: "Gateway Theatre", region: "KwaZulu-Natal", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-07", timestamp: "6:20am", detail: "No response to reminder message.", resolved: false },
  { id: "ex-4", type: "no-show", employeeName: "David Mokoena", site: "Baywest Mall", region: "Eastern Cape", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-07", timestamp: "6:45am", detail: "Reminder message delivery failed. No clock-in.", resolved: false },
  // Supervisor Clock-Ins
  { id: "ex-5", type: "supervisor-clock", employeeName: "Priya Naidoo", site: "Carlton Centre", region: "Gauteng", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-08", timestamp: "6:08am", detail: "Clocked in by Supervisor: Mike Metelerkamp. Employee phone offline.", resolved: false },
  { id: "ex-6", type: "supervisor-clock", employeeName: "Sarah van Wyk", site: "Musgrave Centre", region: "KwaZulu-Natal", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-06", timestamp: "6:12am", detail: "Clocked in by Supervisor: Johan Botha. Employee WhatsApp unavailable.", resolved: false },
  { id: "ex-7", type: "supervisor-clock", employeeName: "Nabeelah Goosen", site: "Warehouse", region: "Western Cape", shift: "Night Shift", shiftTime: "6:00pm - 6:00am", date: "2026-04-05", timestamp: "6:05pm", detail: "Clocked in by Supervisor: Simon Ellis. Employee forgot phone.", resolved: false },
  // No Data after late trigger
  { id: "ex-8", type: "no-data", employeeName: "Johan Botha", site: "All Zones", region: "Western Cape", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-08", timestamp: "7:00am", detail: "No clock event 60 min after shift start. Late trigger exceeded.", resolved: false },
  { id: "ex-9", type: "no-data", employeeName: "Caro van der Merwe", site: "Hatfield Plaza", region: "Gauteng", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-07", timestamp: "7:00am", detail: "No clock event 60 min after shift start. Late trigger exceeded.", resolved: false },
  { id: "ex-10", type: "no-data", employeeName: "Simon Ellis", site: "Hemingways Mall", region: "Eastern Cape", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-06", timestamp: "7:15am", detail: "No clock event 75 min after shift start.", resolved: false },
  { id: "ex-11", type: "no-data", employeeName: "Alexandra Bohmer", site: "Liberty Midlands Mall", region: "KwaZulu-Natal", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-08", timestamp: "7:00am", detail: "No clock event 60 min after shift start.", resolved: false },
  // Geofence Violations
  { id: "ex-12", type: "geofence", employeeName: "Mike Metelerkamp", site: "Rosebank Mall", region: "Gauteng", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-08", timestamp: "6:03am", detail: "Clock-in 340m outside geofence boundary.", resolved: false, geofenceDirection: "in", geofenceDistanceMeters: 340 },
  { id: "ex-13", type: "geofence", employeeName: "Themba Ndlovu", site: "Stockroom", region: "Western Cape", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-07", timestamp: "5:58pm", detail: "Clock-out 1.2km outside geofence boundary.", resolved: false, geofenceDirection: "out", geofenceDistanceMeters: 1200 },
  { id: "ex-14", type: "geofence", employeeName: "David Mokoena", site: "Vincent Park Centre", region: "Eastern Cape", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-05", timestamp: "6:11am", detail: "Clock-in 520m outside geofence boundary.", resolved: false, geofenceDirection: "in", geofenceDistanceMeters: 520 },
  // > 5 km — manual review only, no walking-time estimate
  { id: "ex-15", type: "geofence", employeeName: "Sarah van Wyk", site: "Sandton City", region: "Gauteng", shift: "Day Shift", shiftTime: "6:00am - 6:00pm", date: "2026-04-04", timestamp: "5:47am", detail: "Clock-in 6.8km outside geofence boundary.", resolved: false, geofenceDirection: "in", geofenceDistanceMeters: 6800 },
];

/* ================================================================== */
/*  Mock Data — Site Detail Generator                                  */
/* ================================================================== */

const mockNames = [
  "Alexandra Bohmer", "Simon Ellis", "Jake Smith", "Mike Metelerkamp",
  "Priya Naidoo", "Themba Ndlovu", "Sarah van Wyk", "David Mokoena",
  "Lisa Pieterse", "Johan Botha", "Nabeelah Goosen", "Caro van der Merwe",
];

const mockClients = ["Acme Corp", "Tech Corp", "Servest", "Stallion Security", "City Logistics"];

function generateSiteDetail(siteName: string, areaName: string, regionName: string, scheduled: number): SiteDetail {
  const onsite = Math.round(scheduled * 0.7);
  const client = mockClients[Math.abs(siteName.length * 7) % mockClients.length];
  const noShowCount = Math.max(1, Math.round(scheduled * 0.08));
  const lateCount = Math.max(1, Math.round(scheduled * 0.06));
  const onTimeCount = Math.max(1, onsite - lateCount);
  const earlyCount = Math.max(0, Math.round(scheduled * 0.03));

  const employeeCount = Math.min(scheduled, 8);
  const employees: SiteEmployee[] = Array.from({ length: employeeCount }, (_, i) => {
    const name = mockNames[i % mockNames.length];
    const statusOptions: SiteEmployee["statuses"][] = [
      [{ label: "On Site", variant: "success" }, { label: "Late Arrival", variant: "processing" }],
      [{ label: "Replacement", variant: "processing" }, { label: "No Show", variant: "failed" }],
      [{ label: "On Site", variant: "success" }],
      [{ label: "On Site", variant: "success" }, { label: "Wrong Site", variant: "failed" }],
    ];
    const statuses = statusOptions[i % statusOptions.length];
    const isNoShow = statuses.some((s) => s.label === "No Show");
    const isLate = statuses.some((s) => s.label === "Late Arrival");
    const isReplacement = statuses.some((s) => s.label === "Replacement");
    const isWrongSite = statuses.some((s) => s.label === "Wrong Site");

    const dotColor: SiteEmployee["dotColor"] = isNoShow
      ? "red"
      : isLate
      ? "amber"
      : isReplacement
      ? "orange"
      : isWrongSite
      ? "orange"
      : "green";

    return {
      id: `${siteName}-e${i}`,
      name,
      shift: "Day Shift",
      shiftTime: "6:00am - 6:00pm",
      statuses,
      dotColor,
      clockedIn: isNoShow ? undefined : `${6 + (i % 3)}:0${i % 6}am`,
      clockedOut: i === 0 ? "11:23am" : undefined,
      actualHours: isNoShow ? undefined : `${3 + (i % 5)}.${i % 10}hours`,
      supervisorApproval: i === 0,
      attachment: i === 0 ? "img.jpg" : undefined,
      timelinePercent: isNoShow ? 0 : 30 + (i * 8) % 50,
      replacedBy: isNoShow && i === 2 ? { name: "Mike Kyazze" } : undefined,
    };
  });

  // Build mock roster groups matching the Figma — first one with rich employee data
  const featuredEmployees: SiteEmployee[] = [
    {
      id: `${siteName}-r1-e1`, name: "Alexandra Bohmer", shift: "Day Shift", shiftTime: "6:00am - 6:00pm",
      statuses: [], dotColor: "green",
      clockedIn: "8:06am", clockedInBy: "Supervisor",
      clockedOut: "17:06pm", clockedOutBy: "Admin",
      timelinePercent: 75,
      attachment: "Img.jpg",
    },
    {
      id: `${siteName}-r1-e2`, name: "Amber Smith", shift: "Day Shift", shiftTime: "6:00am - 6:00pm",
      statuses: [], dotColor: "gray",
      timelinePercent: 0,
    },
    {
      id: `${siteName}-r1-e3`, name: "Dawie Blue", shift: "Day Shift", shiftTime: "6:00am - 6:00pm",
      statuses: [], dotColor: "none",
      timelinePercent: 0,
    },
    {
      id: `${siteName}-r1-e4`, name: "Rodrigue S", shift: "Day Shift", shiftTime: "6:00am - 6:00pm",
      statuses: [{ label: "No Show", variant: "failed" }], dotColor: "red",
      timelinePercent: 0,
    },
    {
      id: `${siteName}-r1-e5`, name: "Simon", shift: "Day Shift", shiftTime: "6:00am - 6:00pm",
      statuses: [{ label: "No Show", variant: "failed" }, { label: "Replacement", variant: "processing" }],
      dotColor: "red",
      timelinePercent: 0,
      replacedBy: { name: "Mike Kyazze" },
    },
    {
      id: `${siteName}-r1-e6`, name: "Jake Smith", shift: "Day Shift", shiftTime: "6:00am - 6:00pm",
      statuses: [], dotColor: "amber",
      clockedIn: "8:06am", clockedInBy: "Supervisor",
      clockedOut: "17:06pm",
      timelinePercent: 60,
      attachment: "Img.jpg",
    },
  ];

  const rosterGroups: RosterGroup[] = [
    {
      id: "r1", code: "RD016D", name: "SASOL SHIFT SUPERVISORS", parentSite: "JHB CBD",
      onsite: 15, scheduled: 25, employees: featuredEmployees,
    },
    {
      id: "r2", code: "RD016E", name: "SASOL SHUTTLE DRIVERS", parentSite: "JHB CBD",
      onsite: 15, scheduled: 16, employees: employees.slice(0, 4),
    },
    {
      id: "r3", code: "RD016H", name: "SASOL TACTICAL RESPONSE UNIT", parentSite: "JHB CBD",
      onsite: 10, scheduled: 25, employees: employees.slice(0, 5),
    },
    {
      id: "r4", code: "GM011G", name: "TERACO ISANDO JB 3", parentSite: "Gaut East",
      onsite: 15, scheduled: 25, employees: employees.slice(0, 4),
    },
    {
      id: "r5", code: "RD016J", name: "SASOL NIGHT PATROL", parentSite: "JHB CBD",
      onsite: 8, scheduled: 12, employees: employees.slice(0, 3),
    },
    {
      id: "r6", code: "RD016K", name: "SASOL CONTROL ROOM", parentSite: "JHB CBD",
      onsite: 4, scheduled: 4, employees: employees.slice(0, 2),
    },
  ];

  return {
    name: siteName,
    region: regionName,
    client,
    date: "Wednesday, February 11, 2026",
    totalOnsite: onsite,
    totalScheduled: scheduled,
    shiftCoverageHours: `${(scheduled * 0.8).toFixed(1)}/${scheduled}hrs`,
    shiftCoveragePercent: Math.round((onsite / scheduled) * 100),
    stats: [
      { label: "Early", count: earlyCount, variant: "drafted" },
      { label: "On Time", count: onTimeCount, variant: "success" },
      { label: "Late Arrival", count: lateCount, variant: "processing" },
      { label: "No data", count: Math.max(0, Math.round(scheduled * 0.02)), variant: "drafted" },
      { label: "No Show", count: noShowCount, variant: "failed" },
    ],
    supervisorRequest: scheduled % 3 === 0 ? { name: "Name Surname" } : undefined,
    employees,
    rosterGroups,
  };
}

// Helper to build a SiteRow with auto-generated detail
function makeSite(name: string, area: string, region: string, scheduled: number, clockedIn: number, noShow: number, late: number, adherence: number): SiteRow {
  return {
    name,
    scheduled,
    clockedIn,
    noShow,
    late,
    rosterAdherence: adherence,
    detail: generateSiteDetail(name, area, region, scheduled),
  };
}

/* ================================================================== */
/*  Mock Data — Overview                                               */
/* ================================================================== */

const regions: RegionRow[] = [
  {
    name: "Western Cape",
    color: "bg-amber-400",
    totalSites: 12,
    scheduled: 312,
    clockedIn: 289,
    noShow: "23 (7.4%)",
    late: 18,
    rosterAdherence: 91,
    areas: [
      {
        name: "Cape Town CBD",
        scheduled: 60, clockedIn: 57, noShow: 3, late: 3, rosterAdherence: 95,
        sites: [
          makeSite("Retail Floor", "Cape Town CBD", "Western Cape", 48, 46, 2, 2, 96),
          makeSite("Stockroom", "Cape Town CBD", "Western Cape", 12, 11, 1, 1, 92),
        ],
      },
      {
        name: "Bellville",
        scheduled: 62, clockedIn: 58, noShow: 4, late: 5, rosterAdherence: 94,
        sites: [
          makeSite("Main Floor", "Bellville", "Western Cape", 38, 36, 2, 3, 95),
          makeSite("Warehouse", "Bellville", "Western Cape", 24, 22, 2, 2, 92),
        ],
      },
      {
        name: "Stellenbosch",
        scheduled: 34, clockedIn: 32, noShow: 2, late: 3, rosterAdherence: 94,
        sites: [
          makeSite("Front of House", "Stellenbosch", "Western Cape", 20, 19, 1, 2, 95),
          makeSite("Back of House", "Stellenbosch", "Western Cape", 14, 13, 1, 1, 93),
        ],
      },
      {
        name: "Paarl",
        scheduled: 88, clockedIn: 81, noShow: 7, late: 4, rosterAdherence: 92,
        sites: [
          makeSite("All Zones", "Paarl", "Western Cape", 88, 81, 7, 4, 92),
        ],
      },
      {
        name: "George",
        scheduled: 68, clockedIn: 61, noShow: 7, late: 3, rosterAdherence: 90,
        sites: [
          makeSite("All Zones", "George", "Western Cape", 68, 61, 7, 3, 90),
        ],
      },
    ],
  },
  {
    name: "Gauteng",
    color: "bg-emerald-500",
    totalSites: 18,
    scheduled: 498,
    clockedIn: 475,
    noShow: "23 (4.6%)",
    late: 12,
    rosterAdherence: 96,
    areas: [
      {
        name: "Gauteng South",
        scheduled: 112, clockedIn: 98, noShow: 14, late: 7, rosterAdherence: 88,
        sites: [
          makeSite("Netcare Alberton Hospital", "Gauteng South", "Gauteng", 36, 32, 4, 2, 89),
          makeSite("Alberton City Mall", "Gauteng South", "Gauteng", 28, 25, 3, 2, 89),
          makeSite("Southgate Mall", "Gauteng South", "Gauteng", 24, 21, 3, 1, 88),
          makeSite("Vereeniging Office Park", "Gauteng South", "Gauteng", 24, 20, 4, 2, 83),
        ],
      },
      {
        name: "Pretoria",
        scheduled: 96, clockedIn: 88, noShow: 8, late: 5, rosterAdherence: 92,
        sites: [
          makeSite("Menlyn Maine Offices", "Pretoria", "Gauteng", 32, 30, 2, 1, 94),
          makeSite("Brooklyn Mall", "Pretoria", "Gauteng", 28, 26, 2, 2, 93),
          makeSite("Hatfield Plaza", "Pretoria", "Gauteng", 36, 32, 4, 2, 89),
        ],
      },
      {
        name: "Gauteng Central",
        scheduled: 104, clockedIn: 92, noShow: 12, late: 6, rosterAdherence: 88,
        sites: [
          makeSite("Sandton City", "Gauteng Central", "Gauteng", 40, 36, 4, 2, 90),
          makeSite("Rosebank Mall", "Gauteng Central", "Gauteng", 32, 28, 4, 2, 88),
          makeSite("Midrand Business Park", "Gauteng Central", "Gauteng", 32, 28, 4, 2, 88),
        ],
      },
      {
        name: "JHB CBD",
        scheduled: 88, clockedIn: 74, noShow: 14, late: 6, rosterAdherence: 84,
        sites: [
          makeSite("Carlton Centre", "JHB CBD", "Gauteng", 30, 24, 6, 2, 80),
          makeSite("Gandhi Square Offices", "JHB CBD", "Gauteng", 28, 24, 4, 2, 86),
          makeSite("Marshalltown Depot", "JHB CBD", "Gauteng", 30, 26, 4, 2, 87),
        ],
      },
      {
        name: "Gauteng East",
        scheduled: 98, clockedIn: 89, noShow: 9, late: 4, rosterAdherence: 91,
        sites: [
          makeSite("OR Tambo Logistics Hub", "Gauteng East", "Gauteng", 38, 35, 3, 1, 92),
          makeSite("Boksburg Industrial", "Gauteng East", "Gauteng", 32, 28, 4, 2, 88),
          makeSite("Springs Mall", "Gauteng East", "Gauteng", 28, 26, 2, 1, 93),
        ],
      },
    ],
  },
  {
    name: "KwaZulu-Natal",
    color: "bg-emerald-500",
    totalSites: 6,
    scheduled: 186,
    clockedIn: 178,
    noShow: "8 (4.3%)",
    late: 6,
    rosterAdherence: 96,
    areas: [
      {
        name: "Durban Central",
        scheduled: 102, clockedIn: 98, noShow: 4, late: 3, rosterAdherence: 96,
        sites: [
          makeSite("Gateway Theatre", "Durban Central", "KwaZulu-Natal", 38, 37, 1, 1, 97),
          makeSite("Musgrave Centre", "Durban Central", "KwaZulu-Natal", 32, 30, 2, 1, 94),
          makeSite("Durban ICC", "Durban Central", "KwaZulu-Natal", 32, 31, 1, 1, 97),
        ],
      },
      {
        name: "Pietermaritzburg",
        scheduled: 84, clockedIn: 80, noShow: 4, late: 3, rosterAdherence: 95,
        sites: [
          makeSite("Liberty Midlands Mall", "Pietermaritzburg", "KwaZulu-Natal", 44, 42, 2, 2, 95),
          makeSite("Cascades Office Park", "Pietermaritzburg", "KwaZulu-Natal", 40, 38, 2, 1, 95),
        ],
      },
    ],
  },
  {
    name: "Eastern Cape",
    color: "bg-red-500",
    totalSites: 4,
    scheduled: 128,
    clockedIn: 96,
    noShow: "32 (25%)",
    late: 12,
    rosterAdherence: 78,
    areas: [
      {
        name: "Port Elizabeth",
        scheduled: 72, clockedIn: 54, noShow: 18, late: 7, rosterAdherence: 77,
        sites: [
          makeSite("Baywest Mall", "Port Elizabeth", "Eastern Cape", 40, 30, 10, 4, 75),
          makeSite("Greenacres Shopping Centre", "Port Elizabeth", "Eastern Cape", 32, 24, 8, 3, 78),
        ],
      },
      {
        name: "East London",
        scheduled: 56, clockedIn: 42, noShow: 14, late: 5, rosterAdherence: 79,
        sites: [
          makeSite("Hemingways Mall", "East London", "Eastern Cape", 30, 22, 8, 3, 77),
          makeSite("Vincent Park Centre", "East London", "Eastern Cape", 26, 20, 6, 2, 81),
        ],
      },
    ],
  },
];

// Recalculate adherence: area = avg of sites, region = avg of areas
regions.forEach((region) => {
  if (region.areas.length > 0) {
    region.areas.forEach((area) => {
      if (area.sites.length > 0) {
        area.rosterAdherence = Math.round(
          area.sites.reduce((s, site) => s + site.rosterAdherence, 0) / area.sites.length
        );
      }
    });
    region.rosterAdherence = Math.round(
      region.areas.reduce((s, area) => s + area.rosterAdherence, 0) / region.areas.length
    );
  }
});

const totalScheduled = regions.reduce((s, r) => s + r.scheduled, 0);
const totalClockedIn = regions.reduce((s, r) => s + r.clockedIn, 0);
const totalLate = regions.reduce((s, r) => s + r.late, 0);
const totalSites = regions.reduce((s, r) => s + r.totalSites, 0);
const totalNoShows = totalScheduled - totalClockedIn;
const totalExceptions = totalNoShows + totalLate;
const attendancePct = ((totalClockedIn / totalScheduled) * 100).toFixed(1);
const noShowPct = ((totalNoShows / totalScheduled) * 100).toFixed(1);
const latePct = ((totalLate / totalScheduled) * 100).toFixed(1);

const summaryCards = [
  { label: "Scheduled", value: totalScheduled.toLocaleString(), sub: `Across ${totalSites} sites` },
  { label: "Clocked in", value: totalClockedIn.toLocaleString(), sub: `${attendancePct}% attendance` },
  { label: "No Shows", value: totalNoShows.toLocaleString(), sub: `${noShowPct}% of scheduled` },
  { label: "Late Arrivals", value: totalLate.toLocaleString(), sub: `${latePct}% of scheduled` },
  { label: "Exceptions", value: totalExceptions.toLocaleString(), sub: "across all regions" },
];

/* ================================================================== */
/*  Timeline hours                                                     */
/* ================================================================== */

const shiftHours = [
  "6:00am",
  "7:00am",
  "8:00am",
  "9:00am",
  "10:00am",
  "11:00am",
  "12:00pm",
  "1:00pm",
  "2:00pm",
  "3:00pm",
  "4:00pm",
  "5:00pm",
  "6:00pm",
];

/* ================================================================== */
/*  Sub-components — Overview                                          */
/* ================================================================== */

function SummaryCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="flex-1 rounded-lg border border-greyscale-border bg-white px-md py-sm">
      <p className="text-xs font-medium text-greyscale-text-caption">{label}</p>
      <p className="text-2xl font-semibold text-primary-brand-primary mt-xxxs">{value}</p>
      <p className="text-xs text-greyscale-text-caption mt-xxxs">{sub}</p>
    </div>
  );
}

function AdherenceCell({ value }: { value: number }) {
  if (value === 0) return <span className="text-sm text-greyscale-text-caption">–</span>;
  const tier = value >= 94 ? "good" : value >= 90 ? "warn" : "bad";
  const color = tier === "good" ? "text-emerald-600" : tier === "warn" ? "text-amber-500" : "text-red-500";
  const Icon = tier === "good" ? CheckCircle2 : tier === "warn" ? AlertCircle : AlertTriangle;
  const srLabel = tier === "good" ? "Good adherence: " : tier === "warn" ? "At-risk adherence: " : "Below-target adherence: ";
  return (
    <span className={`text-sm font-semibold inline-flex items-center gap-xxxs ${color}`}>
      <Icon size={12} aria-hidden="true" />
      <span className="sr-only">{srLabel}</span>
      {value}%
    </span>
  );
}

/* ================================================================== */
/*  Sub-components — Site Detail                                       */
/* ================================================================== */

const dotColorClass: Record<SiteEmployee["dotColor"], string> = {
  green: "bg-emerald-500",
  red: "bg-red-500",
  amber: "bg-amber-400",
  orange: "bg-orange-400",
  gray: "bg-gray-300",
  none: "bg-transparent",
};

function EmployeeRow({ employee }: { employee: SiteEmployee }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-greyscale-border bg-white">
      {/* Main row — desktop */}
      <div className="hidden sm:flex items-center justify-between gap-md px-md py-sm">
        <div className="flex flex-col gap-xxxs flex-1 min-w-0">
          {/* Name row */}
          <div className="flex items-center gap-sm">
            <div className="flex items-center gap-xs shrink-0">
              {employee.dotColor !== "none" && (
                <span className={`inline-block w-2 h-2 rounded-full ${dotColorClass[employee.dotColor]}`} aria-hidden="true" />
              )}
              <p className="text-sm font-semibold text-greyscale-text-title">{employee.name}</p>
            </div>

            {/* Replaced by */}
            {employee.replacedBy && (
              <div className="flex items-center gap-xs text-sm text-greyscale-text-caption shrink-0">
                <ArrowRight size={14} />
                <span>Replaced by</span>
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                <span className="font-semibold text-greyscale-text-title">{employee.replacedBy.name}</span>
              </div>
            )}
          </div>

          {/* Shift sub-line + inline clock times */}
          <div className="flex items-center gap-sm text-xs text-greyscale-text-caption">
            <span className="flex items-center gap-xxs">
              <Sun size={12} />
              {employee.shift}: {employee.shiftTime}
            </span>
            {employee.clockedIn && !employee.replacedBy && (
              <>
                <span className="text-greyscale-text-caption">|</span>
                <span className="flex items-center gap-xs">
                  <span>CI: {employee.clockedIn}</span>
                  {employee.clockedInBy && (
                    <Tag variant="drafted" size="sm">by {employee.clockedInBy}</Tag>
                  )}
                  {employee.clockedOut && (
                    <>
                      <span className="text-greyscale-text-caption">|</span>
                      <span>CO: {employee.clockedOut}</span>
                    </>
                  )}
                  {employee.clockedOutBy && (
                    <Tag variant="drafted" size="sm">by {employee.clockedOutBy}</Tag>
                  )}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Right: status tags + icons */}
        <div className="flex items-center gap-xs shrink-0">
          {employee.statuses.map((status, i) => (
            <Tag key={i} variant={status.variant} size="sm">
              {status.label}
            </Tag>
          ))}
          <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-8 sm:w-8 text-amber-600" aria-label="Find replacement">
            <UserSearch size={16} />
          </Button>
          <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-8 sm:w-8" aria-label="Edit employee">
            <Pencil size={14} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 sm:h-8 sm:w-8"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </div>
      </div>

      {/* Main row — phone (collapsed: name + CI/CO + chevron only) */}
      <div className="sm:hidden flex flex-col gap-xxs px-md py-xs">
        <div className="flex items-center justify-between gap-sm">
          <div className="flex items-start gap-xs min-w-0 flex-1">
            {employee.dotColor !== "none" && (
              <span className={`inline-block w-2 h-2 rounded-full shrink-0 mt-[6px] ${dotColorClass[employee.dotColor]}`} aria-hidden="true" />
            )}
            <p className="text-sm font-semibold text-greyscale-text-title break-words">{employee.name}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 sm:h-8 sm:w-8 shrink-0"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </div>

        {/* CI/CO times — collapsed view */}
        {(employee.clockedIn || employee.clockedOut) && (
          <div className="text-xs text-greyscale-text-caption">
            {employee.clockedIn && <span>CI: {employee.clockedIn}</span>}
            {employee.clockedIn && employee.clockedOut && <span className="mx-xs">|</span>}
            {employee.clockedOut && <span>CO: {employee.clockedOut}</span>}
          </div>
        )}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-md pb-md">
          {/* Phone-only: shift + status tags + replaced-by + actions */}
          <div className="sm:hidden flex flex-col gap-sm mb-sm">
            <div className="flex items-center gap-xxs text-xs text-greyscale-text-caption">
              <Sun size={12} />
              <span>{employee.shift}: {employee.shiftTime}</span>
            </div>
            {employee.statuses.length > 0 && (
              <div className="flex flex-wrap items-center gap-xs">
                {employee.statuses.map((status, i) => (
                  <Tag key={i} variant={status.variant} size="sm">
                    {status.label}
                  </Tag>
                ))}
              </div>
            )}
            {employee.replacedBy && (
              <div className="flex flex-wrap items-center gap-xs text-xs text-greyscale-text-caption">
                <ArrowRight size={14} />
                <span>Replaced by</span>
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" aria-hidden="true" />
                <span className="font-semibold text-greyscale-text-title">{employee.replacedBy.name}</span>
              </div>
            )}
            <div className="flex items-center gap-xs">
              <Button variant="outline" size="sm" className="flex-1">
                <UserSearch size={14} className="mr-xxs text-amber-600" />
                Find replacement
              </Button>
              <Button variant="outline" size="sm" className="flex-1">
                <Pencil size={14} className="mr-xxs" />
                Edit
              </Button>
            </div>
          </div>
          {/* Timeline — desktop: bar + hour labels */}
          <div className="hidden sm:block mb-sm">
            <div className="relative h-2 bg-neutral-100 rounded-full overflow-hidden">
              {employee.timelinePercent > 0 && (
                <div
                  className="absolute left-0 top-0 h-full bg-greyscale-text-title rounded"
                  style={{ width: `${employee.timelinePercent}%` }}
                />
              )}
            </div>
            <div className="flex justify-between mt-xxxs">
              {shiftHours.map((h) => (
                <span key={h} className="text-[10px] text-greyscale-text-caption">{h}</span>
              ))}
            </div>
          </div>
          {/* Timeline — phone: compact bar + % label, no hour ticks */}
          <div className="sm:hidden mb-sm">
            <div className="flex items-center justify-between mb-xxxs text-xs text-greyscale-text-caption">
              <span>{employee.shiftTime}</span>
              <span className="font-medium text-greyscale-text-body">
                {employee.timelinePercent}% through shift
              </span>
            </div>
            <div className="relative h-2 bg-neutral-100 rounded-full overflow-hidden">
              {employee.timelinePercent > 0 && (
                <div
                  className="absolute left-0 top-0 h-full bg-greyscale-text-title rounded"
                  style={{ width: `${employee.timelinePercent}%` }}
                />
              )}
            </div>
          </div>

          {/* Not clocked in — shown when employee has no clock event */}
          {!employee.clockedIn && !employee.replacedBy && (
            <p className="text-xs text-greyscale-text-caption mb-xs">Not clocked in</p>
          )}

          {/* Attachment + Approved status — shown when clocked in by supervisor */}
          {(employee.attachment || employee.clockedInBy === "Supervisor") && (
            <div className="flex flex-wrap items-center gap-sm text-xs text-greyscale-text-body">
              <span className="flex items-center gap-xxs">
                Attachment:
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="text-primary-brand-primary underline flex items-center gap-xxs"
                >
                  <Paperclip size={12} />
                  {employee.attachment ?? "Img.jpg"}
                </a>
              </span>
              <span className="flex items-center gap-xxs text-emerald-600">
                <CheckCircle2 size={12} />
                Approved
              </span>
            </div>
          )}

          {employee.replacedBy && employee.clockedIn && (
            <div className="text-xs text-greyscale-text-body space-y-xxxs">
              <p>Clocked in: {employee.clockedIn}</p>
              {employee.clockedOut && <p>Clocked out: {employee.clockedOut}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Site Detail View                                                   */
/* ================================================================== */

function SiteDetailView({
  detail,
  onBack,
  exceptions,
  onResolve,
  onReopen,
}: {
  detail: SiteDetail;
  onBack: () => void;
  exceptions: Exception[];
  onResolve: (id: string) => void;
  onReopen: (id: string) => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [shiftFilter, setShiftFilter] = useState("day-current");
  const [statusFilter, setStatusFilter] = useState("on-time");
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [showSiteExceptions, setShowSiteExceptions] = useState(false);
  const [reportDialogScope, setReportDialogScope] = useState<ReportScope | null>(null);
  const [rostersPage, setRostersPage] = useState(1);
  const [rostersPerPage, setRostersPerPage] = useState(3);

  const allRosterGroups = detail.rosterGroups ?? [];
  const totalRosterPages = Math.max(1, Math.ceil(allRosterGroups.length / rostersPerPage));
  const currentRostersPage = Math.min(rostersPage, totalRosterPages);
  const visibleRosterGroups = allRosterGroups.slice(
    (currentRostersPage - 1) * rostersPerPage,
    currentRostersPage * rostersPerPage,
  );
  const rosterRangeStart = allRosterGroups.length === 0 ? 0 : (currentRostersPage - 1) * rostersPerPage + 1;
  const rosterRangeEnd = Math.min(currentRostersPage * rostersPerPage, allRosterGroups.length);
  const showRosterPagination = allRosterGroups.length > rostersPerPage;

  const siteExceptions = exceptions.filter((e) => e.site === detail.name);
  const unresolvedSiteCount = siteExceptions.filter((e) => !e.resolved).length;

  if (showSiteExceptions) {
    return (
      <ExceptionsView
        exceptions={siteExceptions}
        onBack={() => setShowSiteExceptions(false)}
        onResolve={onResolve}
        onReopen={onReopen}
        siteName={detail.name}
      />
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb className="mb-md">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
              Time &amp; Attendance
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{detail.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Site header with actions */}
      <div className="flex flex-col gap-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-greyscale-text-title">
            {detail.name}
          </h2>
          <div className="flex items-center gap-xxs text-sm text-greyscale-text-caption mt-xxxs">
            <MapPin size={14} />
            <span>{detail.region}</span>
          </div>
        </div>
        <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:gap-sm">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() =>
              setReportDialogScope({
                kind: "site",
                id: `site-${detail.name}`,
                label: detail.name,
                region: detail.region,
              })
            }
          >
            <Download size={14} className="mr-xxs" />
            Download Report
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowSiteExceptions(true)}
            className="relative w-full sm:w-auto"
          >
            <Bell size={14} className="mr-xxs" />
            View Exceptions
            {unresolvedSiteCount > 0 && (
              <Tag variant="failed" size="sm" className="absolute -top-2 -right-2 min-w-[20px] justify-center" aria-label={`${unresolvedSiteCount} unresolved exceptions`}>
                {unresolvedSiteCount}
              </Tag>
            )}
          </Button>
          <Button variant="default" className="w-full sm:w-auto">
            Clock In/Out Employees
          </Button>
        </div>
      </div>

      {/* Date */}
      <p className="text-sm text-greyscale-text-body mt-sm mb-md">
        Date: <span className="font-semibold">{detail.date}</span>
      </p>

      <Divider className="mb-md" />

      {/* Search */}
      <SearchInput
        placeholder="Search employee, on site, tags etc"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClear={() => setSearchQuery("")}
        className="w-full mb-md"
      />

      {/* Filters */}
      <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:gap-sm mb-md">
        <Select defaultValue="all">
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Rosters</SelectItem>
            {detail.rosterGroups?.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={shiftFilter} onValueChange={setShiftFilter}>
          <SelectTrigger className="w-full sm:w-[200px] whitespace-nowrap">
            <SelectValue placeholder="Day Shift (Current)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day-current">Day Shift (Current)</SelectItem>
            <SelectItem value="night">Night Shift</SelectItem>
            <SelectItem value="all">All Shifts</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue placeholder="On Time" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="on-time">On Time</SelectItem>
            <SelectItem value="late">Late Arrival</SelectItem>
            <SelectItem value="no-show">No Show</SelectItem>
            <SelectItem value="all">All Statuses</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats row — simplified (no tag row) */}
      <div className="flex items-center flex-wrap gap-x-md gap-y-xs text-sm text-greyscale-text-body mb-md">
        <span className="flex items-center gap-xxs">
          <UsersIcon size={14} className="text-greyscale-text-caption" />
          {detail.totalOnsite} Of {detail.totalScheduled} Onsite
        </span>
        <span className="flex items-center gap-xxs">
          <Clock size={14} className="text-greyscale-text-caption" />
          Shift Coverage: {detail.shiftCoverageHours} ({detail.shiftCoveragePercent}%)
        </span>
      </div>

      {/* Supervisor alert banner */}
      {detail.supervisorRequest && (
        <Alert variant="warning" className="mb-md !flex-col items-stretch gap-sm sm:!flex-row sm:items-center !border-orange-200" hideIcon>
          <div className="flex items-start gap-sm flex-1">
            <AlertCircle size={16} className="text-amber-600 shrink-0 mt-xxxs" />
            <div className="flex-1">
              <p className="font-semibold text-sm text-greyscale-text-title leading-tight">
                New Supervisor Employee Clock in
              </p>
              <AlertDescription className="pl-0 text-xs">
                Please review and approve the employee clock in by Supervisor: [{detail.supervisorRequest.name}]
              </AlertDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 w-full sm:w-auto"
            onClick={() => setBulkModalOpen(true)}
          >
            View Request
          </Button>
        </Alert>
      )}

      {/* Bulk supervisor clock-in approval modal */}
      {detail.supervisorRequest && (
        <BulkSupervisorClockInModal
          open={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          supervisorName={detail.supervisorRequest.name}
          dateTime={detail.date}
          onApprove={() => setBulkModalOpen(false)}
        />
      )}

      {/* Roster groups — accordion sections */}
      <Accordion
        type="multiple"
        defaultValue={allRosterGroups.length ? [allRosterGroups[0].id] : []}
      >
        {visibleRosterGroups.map((group) => {
          const pct = Math.round((group.onsite / group.scheduled) * 100);
          return (
            <AccordionItem key={group.id} value={group.id}>
              <AccordionTrigger className="py-sm text-sm font-semibold text-greyscale-text-title hover:no-underline gap-sm">
                <div className="flex flex-col items-start gap-xxxs text-left sm:flex-row sm:items-center sm:gap-xs whitespace-normal">
                  <span>{group.parentSite} ({group.code}): {group.name}</span>
                  <span className="font-regular text-greyscale-text-caption">({group.onsite}/{group.scheduled}) {pct}%</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-md">
                <div className="flex items-center justify-end mb-xs">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setReportDialogScope({
                        kind: "roster",
                        id: group.id,
                        label: `${group.parentSite} (${group.code}): ${group.name}`,
                        parentSite: group.parentSite,
                      })
                    }
                  >
                    <Download size={14} className="mr-xxs" />
                    Download Report
                  </Button>
                </div>
                <div className="space-y-sm">
                  {group.employees.map((emp) => (
                    <EmployeeRow key={emp.id} employee={emp} />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Pagination — only shown when there's more than one page */}
      {showRosterPagination && (
        <div className="flex items-center justify-end flex-wrap gap-x-md gap-y-xs mt-md">
          <div className="hidden sm:flex items-center gap-xs text-sm text-greyscale-text-caption">
            <span>Entries per page:</span>
            <Select
              value={String(rostersPerPage)}
              onValueChange={(v) => {
                setRostersPerPage(Number(v));
                setRostersPage(1);
              }}
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3</SelectItem>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-xs ml-auto sm:ml-0">
            <span className="text-sm text-greyscale-text-caption">
              {rosterRangeStart}–{rosterRangeEnd} of {allRosterGroups.length}
            </span>
            <div className="flex items-center gap-xxxs">
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 sm:h-8 sm:w-8"
                disabled={currentRostersPage <= 1}
                onClick={() => setRostersPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 sm:h-8 sm:w-8"
                disabled={currentRostersPage >= totalRosterPages}
                onClick={() => setRostersPage((p) => Math.min(totalRosterPages, p + 1))}
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Report Wizard Dialog — opens from site header or per-roster Download Report */}
      {reportDialogScope && (
        <ReportWizardDialog
          open={reportDialogScope !== null}
          onOpenChange={(open) => {
            if (!open) setReportDialogScope(null);
          }}
          scope={reportDialogScope}
        />
      )}
      <Toaster position="bottom-right" />
    </div>
  );
}

/* ================================================================== */
/*  Exception Action Modals                                            */
/* ================================================================== */

function NoShowActionModal({
  exception,
  open,
  onClose,
  onResolve,
}: {
  exception: Exception;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string) => void;
}) {
  const [action, setAction] = useState<string>("");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw_-_1rem)] max-w-none sm:w-full sm:max-w-[480px] max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <UserX size={18} className="text-red-500" />
            No Show — Resolve Exception
          </DialogTitle>
          <DialogDescription>
            {exception.employeeName} did not clock in for their shift at {exception.site}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <p className="text-sm"><span className="font-semibold">Employee:</span> {exception.employeeName}</p>
            <p className="text-sm"><span className="font-semibold">Site:</span> {exception.site} — {exception.region}</p>
            <p className="text-sm"><span className="font-semibold">Shift:</span> {exception.shift} ({exception.shiftTime})</p>
            <p className="text-sm text-greyscale-text-caption">{exception.detail}</p>
          </div>

          <div className="space-y-xs">
            <Label className="text-sm font-semibold">Choose action</Label>
            <RadioGroup value={action} onValueChange={setAction}>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="replacement" id="no-show-replacement" />
                <Label htmlFor="no-show-replacement" className="text-sm">Find Replacement</Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="sick" id="no-show-sick" />
                <Label htmlFor="no-show-sick" className="text-sm">Mark as Sick</Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="family" id="no-show-family" />
                <Label htmlFor="no-show-family" className="text-sm">Mark as Family Responsibility</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-full sm:w-auto">Cancel</Button>
          </DialogClose>
          <Button
            disabled={!action}
            className="w-full sm:w-auto"
            onClick={() => {
              onResolve(exception.id);
              onClose();
            }}
          >
            {action === "replacement" ? "Find Replacement" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SupervisorClockModal({
  exception,
  open,
  onClose,
  onResolve,
}: {
  exception: Exception;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string) => void;
}) {
  const [rejectReason, setRejectReason] = useState("");
  const [choice, setChoice] = useState<"approve" | "reject" | "">("");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw_-_1rem)] max-w-none sm:w-full sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <ShieldCheck size={18} className="text-amber-500" />
            Supervisor Clock-In — Review
          </DialogTitle>
          <DialogDescription>
            Review the supervisor-captured clock-in for {exception.employeeName}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          {/* Supervisor proof image */}
          <div className="rounded-lg overflow-hidden border border-greyscale-border bg-neutral-50">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
              alt={`Proof of clock-in by supervisor for ${exception.employeeName}`}
              className="w-full h-[180px] sm:h-[240px] object-cover"
            />
            <div className="flex items-center justify-between px-sm py-xs text-xs text-greyscale-text-caption">
              <span className="flex items-center gap-xxs">
                <Paperclip size={12} />
                supervisor-clock-in-{exception.id}.jpg
              </span>
              <span>Uploaded by supervisor at {exception.timestamp}</span>
            </div>
          </div>

          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <p className="text-sm"><span className="font-semibold">Employee:</span> {exception.employeeName}</p>
            <p className="text-sm"><span className="font-semibold">Site:</span> {exception.site} — {exception.region}</p>
            <p className="text-sm"><span className="font-semibold">Shift:</span> {exception.shift} ({exception.shiftTime})</p>
            <p className="text-sm"><span className="font-semibold">Clock time:</span> {exception.timestamp}</p>
            <p className="text-sm text-greyscale-text-caption">{exception.detail}</p>
          </div>

          <div className="space-y-xs">
            <Label className="text-sm font-semibold">Decision</Label>
            <RadioGroup value={choice} onValueChange={(v) => setChoice(v as "approve" | "reject")}>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="approve" id="sup-approve" />
                <Label htmlFor="sup-approve" className="text-sm">Approve clock-in</Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="reject" id="sup-reject" />
                <Label htmlFor="sup-reject" className="text-sm">Reject clock-in</Label>
              </div>
            </RadioGroup>
          </div>

          {choice === "reject" && (
            <div className="space-y-xxs">
              <Label className="text-sm font-semibold">Reason for rejection</Label>
              <Textarea
                placeholder="Provide a reason for rejecting this clock-in..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-full sm:w-auto">Cancel</Button>
          </DialogClose>
          <Button
            disabled={!choice || (choice === "reject" && !rejectReason.trim())}
            variant={choice === "reject" ? "destructive" : "default"}
            className="w-full sm:w-auto"
            onClick={() => {
              onResolve(exception.id);
              onClose();
            }}
          >
            {choice === "approve" ? "Approve" : choice === "reject" ? "Reject" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoDataActionModal({
  exception,
  open,
  onClose,
  onResolve,
}: {
  exception: Exception;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string) => void;
}) {
  const [action, setAction] = useState<string>("");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw_-_1rem)] max-w-none sm:w-full sm:max-w-[480px] max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <CircleOff size={18} className="text-gray-500" />
            No Data — Resolve Status
          </DialogTitle>
          <DialogDescription>
            No clock event recorded for {exception.employeeName} after the late trigger.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <p className="text-sm"><span className="font-semibold">Employee:</span> {exception.employeeName}</p>
            <p className="text-sm"><span className="font-semibold">Site:</span> {exception.site} — {exception.region}</p>
            <p className="text-sm"><span className="font-semibold">Shift:</span> {exception.shift} ({exception.shiftTime})</p>
            <p className="text-sm"><span className="font-semibold">Late trigger at:</span> {exception.timestamp}</p>
            <p className="text-sm text-greyscale-text-caption">{exception.detail}</p>
          </div>

          <div className="space-y-xs">
            <Label className="text-sm font-semibold">Resolve as</Label>
            <RadioGroup value={action} onValueChange={setAction}>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="no-show" id="nodata-noshow" />
                <Label htmlFor="nodata-noshow" className="text-sm">No Show — employee did not arrive</Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="replaced" id="nodata-replaced" />
                <Label htmlFor="nodata-replaced" className="text-sm">Replaced — a replacement was assigned</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-full sm:w-auto">Cancel</Button>
          </DialogClose>
          <Button
            disabled={!action}
            className="w-full sm:w-auto"
            onClick={() => {
              onResolve(exception.id);
              onClose();
            }}
          >
            Resolve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GeofenceActionModal({
  exception,
  open,
  onClose,
  onResolve,
}: {
  exception: Exception;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string) => void;
}) {
  const direction = exception.geofenceDirection ?? "in";
  const distance = exception.geofenceDistanceMeters ?? 0;
  const estimate = computeGeofenceAdjustedTime(exception.timestamp, direction, distance);
  const beyondCap = estimate === null;

  const [action, setAction] = useState<"confirm" | "no-show">("confirm");
  const [adjustedTime, setAdjustedTime] = useState(estimate?.adjusted ?? exception.timestamp);
  const [reason, setReason] = useState("");

  // Reset per-exception when the modal opens against a new row.
  useEffect(() => {
    setAdjustedTime(estimate?.adjusted ?? exception.timestamp);
    setAction("confirm");
    setReason("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exception.id, open]);

  const directionLabel = direction === "in" ? "clock-in" : "clock-out";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="w-[calc(100vw_-_1rem)] max-w-none sm:w-full sm:max-w-[520px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <MapPinOff size={18} className="text-red-500" />
            Geofence Violation — Review
          </DialogTitle>
          <DialogDescription>
            {exception.employeeName} clocked {direction === "in" ? "in" : "out"}{" "}
            {formatGeofenceDistance(distance)} outside the {exception.site} geofence.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          {/* Context — what we know */}
          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <p className="text-sm"><span className="font-semibold">Employee:</span> {exception.employeeName}</p>
            <p className="text-sm"><span className="font-semibold">Site:</span> {exception.site} — {exception.region}</p>
            <p className="text-sm"><span className="font-semibold">Shift:</span> {exception.shift} ({exception.shiftTime})</p>
            <p className="text-sm">
              <span className="font-semibold">Original {directionLabel}:</span> {exception.timestamp}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Distance outside:</span> {formatGeofenceDistance(distance)}
            </p>
          </div>

          {/* Manual-review alert when beyond the 5 km cap */}
          {beyondCap && (
            <Alert variant="destructive">
              <AlertTitle>Manual review required</AlertTitle>
              <AlertDescription>
                {formatGeofenceDistance(distance)} is beyond the {formatGeofenceDistance(GEOFENCE_DISTANCE_CAP_METERS)} walking-estimate cap.
                The employee may be in a vehicle, at the wrong site, or the GPS may be malfunctioning. Set the {directionLabel} time
                manually below, or mark as No Show.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-xs">
            <Label className="text-sm font-semibold">Action</Label>
            <RadioGroup
              value={action}
              onValueChange={(v) => setAction(v as "confirm" | "no-show")}
            >
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="confirm" id="geo-confirm" />
                <Label htmlFor="geo-confirm" className="text-sm">
                  Confirm {directionLabel} time
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="no-show" id="geo-noshow" />
                <Label htmlFor="geo-noshow" className="text-sm">Mark as No Show</Label>
              </div>
            </RadioGroup>
          </div>

          {action === "confirm" && (
            <div className="space-y-xxs">
              <Label htmlFor="geo-adjusted-time" className="text-sm font-semibold">
                Adjusted {directionLabel} time
              </Label>
              <InputField
                id="geo-adjusted-time"
                value={adjustedTime}
                onChange={(e) => setAdjustedTime(e.target.value)}
                placeholder="e.g. 6:08am"
              />
              {!beyondCap && estimate ? (
                <p className="text-xs text-greyscale-text-caption">
                  Estimated from {exception.timestamp} {direction === "in" ? "+" : "−"}{" "}
                  {estimate.deltaMinutes} min walking time at {GEOFENCE_WALKING_SPEED_M_PER_MIN} m/min
                  ({formatGeofenceDistance(distance)}). Edit if you have better information.
                </p>
              ) : (
                <p className="text-xs text-greyscale-text-caption">
                  No estimate offered — set the time manually based on what you know about this clock event.
                </p>
              )}
            </div>
          )}

          {action === "no-show" && (
            <div className="space-y-xxs">
              <Label htmlFor="geo-reason" className="text-sm font-semibold">Reason</Label>
              <Textarea
                id="geo-reason"
                placeholder="Provide a reason for marking as no show..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          )}

          {/* Audit-trail note — surfaces the override contract in the UI */}
          <Alert variant="note">
            <AlertTitle>This overrides the original clock time</AlertTitle>
            <AlertDescription>
              Your confirmation is recorded with your user ID and a timestamp. Both the original clock and the
              confirmed clock remain queryable in the audit log.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-full sm:w-auto">Cancel</Button>
          </DialogClose>
          <Button
            disabled={
              (action === "confirm" && !adjustedTime.trim()) ||
              (action === "no-show" && !reason.trim())
            }
            variant={action === "no-show" ? "destructive" : "default"}
            className="w-full sm:w-auto"
            onClick={() => {
              onResolve(exception.id);
              onClose();
            }}
          >
            {action === "confirm" ? "Confirm clock time" : "Confirm No Show"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== */
/*  Bulk Supervisor Clock-In Approval Modal                            */
/* ================================================================== */

interface BulkClockInEmployee {
  id: string;
  name: string;
}

const BULK_SUPERVISOR_EMPLOYEES: BulkClockInEmployee[] = [
  { id: "b1", name: "Alex Banting" },
  { id: "b2", name: "Rodrigue Smith" },
  { id: "b3", name: "Richard Barns" },
  { id: "b4", name: "Thams Sithole" },
  { id: "b5", name: "Kelly Stevens" },
];

function BulkSupervisorClockInModal({
  open,
  onClose,
  supervisorName,
  dateTime,
  onApprove,
}: {
  open: boolean;
  onClose: () => void;
  supervisorName: string;
  dateTime: string;
  onApprove: (approvedIds: string[]) => void;
}) {
  const [step, setStep] = useState<"review" | "confirm">("review");
  const [selected, setSelected] = useState<Set<string>>(
    new Set(BULK_SUPERVISOR_EMPLOYEES.map((e) => e.id))
  );

  const allSelected = selected.size === BULK_SUPERVISOR_EMPLOYEES.length;

  const approvedEmployees = BULK_SUPERVISOR_EMPLOYEES.filter((e) => selected.has(e.id));
  const declinedEmployees = BULK_SUPERVISOR_EMPLOYEES.filter((e) => !selected.has(e.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(BULK_SUPERVISOR_EMPLOYEES.map((e) => e.id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClose = () => {
    setStep("review");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100vw_-_1rem)] max-w-none sm:w-full sm:max-w-[680px] max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Approve employee clock in by Supervisor on site</DialogTitle>
        </DialogHeader>

        {step === "review" ? (
          <>
            <div className="space-y-md py-sm">
              {/* Supervisor proof image with tagged employees */}
              <div className="rounded-lg overflow-hidden border border-greyscale-border">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                  alt={`Group photo taken by ${supervisorName} on site`}
                  className="w-full h-[200px] sm:h-[320px] object-cover"
                />
              </div>

              {/* Meta row */}
              <div className="grid grid-cols-1 gap-xs sm:grid-cols-3 sm:gap-md text-sm">
                <div>
                  <p className="text-xs text-greyscale-text-caption mb-xxxs">Date/Time:</p>
                  <p className="font-semibold text-greyscale-text-title">{dateTime}</p>
                </div>
                <div>
                  <p className="text-xs text-greyscale-text-caption mb-xxxs">Supervisor:</p>
                  <p className="font-semibold text-greyscale-text-title">{supervisorName}</p>
                </div>
                <div>
                  <p className="text-xs text-greyscale-text-caption mb-xxxs">Tagged Employee/s:</p>
                  <p className="font-semibold text-greyscale-text-title">
                    {BULK_SUPERVISOR_EMPLOYEES.length} tagged
                  </p>
                </div>
              </div>

              {/* Approve employees list */}
              <div className="space-y-sm">
                <div className="flex items-center gap-xs">
                  <Checkbox
                    id="approve-all"
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                  />
                  <Label htmlFor="approve-all" className="text-sm font-semibold">
                    Approve Employees ({BULK_SUPERVISOR_EMPLOYEES.length}):
                  </Label>
                </div>

                <div className="grid grid-cols-2 gap-x-md gap-y-xs pl-lg">
                  {BULK_SUPERVISOR_EMPLOYEES.map((emp) => (
                    <div key={emp.id} className="flex items-center gap-xs">
                      <Checkbox
                        id={`bulk-${emp.id}`}
                        checked={selected.has(emp.id)}
                        onCheckedChange={() => toggleOne(emp.id)}
                      />
                      <Label htmlFor={`bulk-${emp.id}`} className="text-sm">
                        {emp.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" className="w-full sm:w-auto">Cancel</Button>
              </DialogClose>
              <Button
                className="w-full sm:w-auto"
                disabled={selected.size === 0}
                onClick={() => setStep("confirm")}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-md py-sm">
              {/* Meta */}
              <div className="space-y-xs text-sm">
                <div>
                  <p className="text-xs text-greyscale-text-caption mb-xxxs">Date/Time:</p>
                  <p className="font-semibold text-greyscale-text-title">{dateTime}</p>
                </div>
                <div>
                  <p className="text-xs text-greyscale-text-caption mb-xxxs">Supervisor:</p>
                  <p className="font-semibold text-greyscale-text-title">{supervisorName}</p>
                </div>
              </div>

              {/* Approved list */}
              {approvedEmployees.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-greyscale-text-title mb-xxs">
                    Approved Employees ({approvedEmployees.length})
                  </p>
                  <ul className="space-y-xxxs pl-xs">
                    {approvedEmployees.map((emp) => (
                      <li key={emp.id} className="text-sm text-greyscale-text-body">
                        {emp.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Declined list */}
              {declinedEmployees.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-greyscale-text-title mb-xxs">
                    Declined Employees ({declinedEmployees.length}):
                  </p>
                  <ul className="space-y-xxxs pl-xs">
                    {declinedEmployees.map((emp) => (
                      <li key={emp.id} className="text-sm text-greyscale-text-body">
                        {emp.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" className="w-full sm:w-auto" onClick={() => setStep("review")}>
                Go Back
              </Button>
              <Button
                className="w-full sm:w-auto"
                onClick={() => {
                  onApprove(Array.from(selected));
                  setStep("review");
                  onClose();
                }}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== */
/*  Exceptions List View                                               */
/* ================================================================== */

function ExceptionsView({
  exceptions,
  onBack,
  onResolve,
  onReopen,
  siteName,
}: {
  exceptions: Exception[];
  onBack: () => void;
  onResolve: (id: string) => void;
  onReopen: (id: string) => void;
  siteName?: string;
}) {
  const [typeFilter, setTypeFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState<Date | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedExc, setSelectedExc] = useState<Exception | null>(null);

  const unresolvedCount = exceptions.filter((e) => !e.resolved).length;

  // Column definitions for unresolved exceptions table
  const unresolvedColumns: ColumnDef<Exception>[] = [
    {
      accessorKey: "date",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">{formatDateStr(row.original.date)}</span>
      ),
    },
    {
      accessorKey: "employeeName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee" />,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-greyscale-text-title">{row.original.employeeName}</span>
      ),
    },
    {
      accessorKey: "type",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => (
        <Tag variant={exceptionTypeVariants[row.original.type]} size="sm">
          {exceptionTypeLabels[row.original.type]}
        </Tag>
      ),
    },
    {
      accessorKey: "site",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Site" />,
    },
    {
      accessorKey: "region",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Region" />,
    },
    {
      accessorKey: "timestamp",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Time" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-caption">{row.original.timestamp}</span>
      ),
    },
    {
      id: "actions",
      header: () => <span className="text-center block">Action</span>,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => setSelectedExc(row.original)}>
            Resolve
          </Button>
        </div>
      ),
    },
  ];

  // Column definitions for resolved exceptions table
  const resolvedColumns: ColumnDef<Exception>[] = [
    {
      accessorKey: "date",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-caption">{formatDateStr(row.original.date)}</span>
      ),
    },
    {
      accessorKey: "employeeName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-caption">{row.original.employeeName}</span>
      ),
    },
    {
      accessorKey: "type",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => (
        <Tag variant="success" size="sm">
          {exceptionTypeLabels[row.original.type]}
        </Tag>
      ),
    },
    {
      accessorKey: "site",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Site" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-caption">{row.original.site}</span>
      ),
    },
    {
      accessorKey: "region",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Region" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-caption">{row.original.region}</span>
      ),
    },
    {
      accessorKey: "timestamp",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Time" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-caption">{row.original.timestamp}</span>
      ),
    },
    {
      id: "actions",
      header: () => <span className="text-center block">Action</span>,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => onReopen(row.original.id)}>
            Reopen
          </Button>
        </div>
      ),
    },
  ];

  // Get unique sites for filter
  const sites = Array.from(new Set(exceptions.map((e) => e.site))).sort();

  // Filter exceptions
  const filtered = exceptions.filter((e) => {
    if (e.resolved) return false;
    if (typeFilter !== "all" && e.type !== typeFilter) return false;
    if (siteFilter !== "all" && e.site !== siteFilter) return false;
    if (dateFilter) {
      const excDate = parseDateStr(e.date);
      if (excDate.toDateString() !== dateFilter.toDateString()) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        e.employeeName.toLowerCase().includes(q) ||
        e.site.toLowerCase().includes(q) ||
        e.region.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Count by type
  const countByType = (type: ExceptionType) => exceptions.filter((e) => !e.resolved && e.type === type).length;

  return (
    <div className="pb-xl">
      {/* Breadcrumb */}
      <Breadcrumb className="mb-md">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
              {siteName || "Time & Attendance"}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Exceptions</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex flex-col gap-sm mb-md sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-greyscale-text-title flex items-center flex-wrap gap-xs">
            <AlertTriangle size={20} className="text-amber-500" />
            {siteName ? `${siteName} — Exceptions` : "Exceptions"}
            <Tag variant="failed" size="sm" aria-label={`${unresolvedCount} unresolved exceptions`}>
              {unresolvedCount}
            </Tag>
          </h2>
          <p className="text-sm text-greyscale-text-body mt-xxxs">
            {siteName ? `Resolve attendance exceptions for ${siteName}.` : "Resolve attendance exceptions across all sites."}
          </p>
        </div>
        <Button variant="outline" onClick={onBack} className="w-full sm:w-auto">
          {siteName ? `Back to ${siteName}` : "Back to Overview"}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-xs mb-lg sm:flex-row sm:items-center sm:justify-between sm:gap-sm">
        <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:gap-xxs">
          <DatePicker
            date={dateFilter}
            onDateChange={setDateFilter}
            placeholder="Filter by date"
          />

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Exception Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Exception Types</SelectItem>
              <SelectItem value="no-show">No Show</SelectItem>
              <SelectItem value="supervisor-clock">Supervisor Clock-In</SelectItem>
              <SelectItem value="no-data">No Data</SelectItem>
              <SelectItem value="geofence">Geofence Violation</SelectItem>
            </SelectContent>
          </Select>

          {!siteName && (
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Sites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sites.map((site) => (
                  <SelectItem key={site} value={site}>{site}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <SearchInput
          placeholder="Search employee, site, region"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
          className="w-full sm:w-[280px]"
        />
      </div>

      {/* Summary tags */}
      <div className="flex items-center flex-wrap justify-start gap-xxs mb-xs sm:justify-end">
        <Tag variant="failed" size="sm">
          No Shows: {countByType("no-show")}
        </Tag>
        <Tag variant="processing" size="sm">
          Supervisor Clock-Ins: {countByType("supervisor-clock")}
        </Tag>
        <Tag variant="drafted" size="sm">
          No Data: {countByType("no-data")}
        </Tag>
        <Tag variant="failed" size="sm">
          Geofence: {countByType("geofence")}
        </Tag>
      </div>

      {/* Unresolved Exceptions — desktop table */}
      <div className="hidden sm:block">
        <DataTable
          columns={unresolvedColumns}
          data={filtered}
          showPagination
          showToolbar={false}
        />
      </div>
      {/* Unresolved Exceptions — phone card list */}
      <div className="sm:hidden flex flex-col gap-sm">
        {filtered.map((exc) => (
          <button
            key={exc.id}
            type="button"
            onClick={() => setSelectedExc(exc)}
            className="w-full rounded-lg border border-greyscale-border bg-white p-sm text-left shadow-sm"
          >
            <div className="flex items-start justify-between gap-sm">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-greyscale-text-caption">{formatDateStr(exc.date)} · {exc.timestamp}</p>
                <p className="text-sm font-semibold text-greyscale-text-title mt-xxxs truncate">{exc.employeeName}</p>
                <p className="text-xs text-greyscale-text-caption mt-xxxs truncate">{exc.site} · {exc.region}</p>
              </div>
              <Tag variant={exceptionTypeVariants[exc.type]} size="sm" className="shrink-0">
                {exceptionTypeLabels[exc.type]}
              </Tag>
            </div>
            <div className="flex items-center justify-end gap-xxs mt-sm text-xs font-semibold text-primary-brand-primary">
              Resolve
              <ChevronRight size={14} />
            </div>
          </button>
        ))}
      </div>
      {filtered.length === 0 && (
        <p className="text-center text-sm text-greyscale-text-caption py-md">
          No unresolved exceptions match your filters.
        </p>
      )}

      {/* Resolved History */}
      {(() => {
        const resolved = exceptions.filter((e) => e.resolved);
        if (resolved.length === 0) return null;
        return (
          <div className="mt-lg">
            <h3 className="font-heading text-base font-semibold text-greyscale-text-title flex items-center gap-xs mb-sm">
              <CheckCircle2 size={16} className="text-emerald-500" />
              Resolved ({resolved.length})
            </h3>
            {/* Resolved — desktop table */}
            <div className="opacity-75 hidden sm:block">
              <DataTable
                columns={resolvedColumns}
                data={resolved}
                showPagination
                showToolbar={false}
              />
            </div>
            {/* Resolved — phone card list */}
            <div className="opacity-75 sm:hidden flex flex-col gap-sm">
              {resolved.map((exc) => (
                <div
                  key={exc.id}
                  className="rounded-lg border border-greyscale-border bg-white p-sm"
                >
                  <div className="flex items-start justify-between gap-sm">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-greyscale-text-caption">{formatDateStr(exc.date)} · {exc.timestamp}</p>
                      <p className="text-sm text-greyscale-text-caption mt-xxxs truncate">{exc.employeeName}</p>
                      <p className="text-xs text-greyscale-text-caption mt-xxxs truncate">{exc.site} · {exc.region}</p>
                    </div>
                    <Tag variant="success" size="sm" className="shrink-0">
                      {exceptionTypeLabels[exc.type]}
                    </Tag>
                  </div>
                  <div className="flex justify-end mt-sm">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onReopen(exc.id)}
                    >
                      Reopen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Action Modals */}
      {selectedExc?.type === "no-show" && (
        <NoShowActionModal
          exception={selectedExc}
          open={!!selectedExc}
          onClose={() => setSelectedExc(null)}
          onResolve={onResolve}
        />
      )}
      {selectedExc?.type === "supervisor-clock" && (
        <SupervisorClockModal
          exception={selectedExc}
          open={!!selectedExc}
          onClose={() => setSelectedExc(null)}
          onResolve={onResolve}
        />
      )}
      {selectedExc?.type === "no-data" && (
        <NoDataActionModal
          exception={selectedExc}
          open={!!selectedExc}
          onClose={() => setSelectedExc(null)}
          onResolve={onResolve}
        />
      )}
      {selectedExc?.type === "geofence" && (
        <GeofenceActionModal
          exception={selectedExc}
          open={!!selectedExc}
          onClose={() => setSelectedExc(null)}
          onResolve={onResolve}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/*  Main Page — Overview                                               */
/* ================================================================== */

export interface TimeAttendanceProps {
  onNavigate?: (page: string) => void;
}

export function TimeAttendance({ onNavigate }: TimeAttendanceProps = {}) {
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set(["Western Cape"]));
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set(["Western Cape::Cape Town CBD"]));
  const [dateFilter, setDateFilter] = useState("today");
  const [clientFilter, setClientFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSite, setSelectedSite] = useState<SiteDetail | null>(null);
  const [showExceptions, setShowExceptions] = useState(false);
  const [exceptions, setExceptions] = useState<Exception[]>(mockExceptions);

  const unresolvedExceptionCount = exceptions.filter((e) => !e.resolved).length;

  const handleResolveException = (id: string) => {
    setExceptions((prev) =>
      prev.map((e) => (e.id === id ? { ...e, resolved: true } : e))
    );
  };

  const handleReopenException = (id: string) => {
    setExceptions((prev) =>
      prev.map((e) => (e.id === id ? { ...e, resolved: false } : e))
    );
  };

  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Exceptions view
  if (showExceptions) {
    return (
      <ExceptionsView
        exceptions={exceptions}
        onBack={() => setShowExceptions(false)}
        onResolve={handleResolveException}
        onReopen={handleReopenException}
      />
    );
  }

  // Site detail view
  if (selectedSite) {
    return (
      <SiteDetailView
        detail={selectedSite}
        onBack={() => setSelectedSite(null)}
        exceptions={exceptions}
        onResolve={handleResolveException}
        onReopen={handleReopenException}
      />
    );
  }

  // Overview
  return (
    <div>
      {/* Page header */}
      <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
        Time &amp; Attendance
      </h1>
      <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
        Track attendance in real time and monitor hours worked on site.
      </p>

      {/* Filters row */}
      <div className="flex flex-col gap-xs mt-md mb-md sm:flex-row sm:items-center sm:justify-between sm:gap-sm">
        <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:gap-sm">
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue placeholder="Today" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this-week">This Week</SelectItem>
              <SelectItem value="last-week">Last Week</SelectItem>
            </SelectContent>
          </Select>

          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              <SelectItem value="client-1">Stallion Security</SelectItem>
              <SelectItem value="client-2">City Logistics</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <SearchInput
          placeholder="Search client, site, location"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
          className="w-full sm:w-[280px]"
        />
      </div>

      {/* Date label + View Exceptions */}
      <div className="flex flex-col gap-xs mb-md sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-greyscale-text-body">
          Showing data for: <span className="font-semibold">Thursday, February 12, 2026</span>
        </p>
        <Button variant="default" onClick={() => setShowExceptions(true)} className="relative w-full sm:w-auto">
          <Bell size={14} className="mr-xxs" />
          View Exceptions
          {unresolvedExceptionCount > 0 && (
            <Tag variant="failed" size="sm" className="absolute -top-2 -right-2 min-w-[20px] justify-center" aria-label={`${unresolvedExceptionCount} unresolved exceptions`}>
              {unresolvedExceptionCount}
            </Tag>
          )}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-sm mb-lg sm:flex sm:gap-sm">
        {summaryCards.map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
      </div>

      {/* Card list — phone only */}
      <div className="sm:hidden flex flex-col gap-md">
        {regions.map((region) => {
          const regionExpanded = expandedRegions.has(region.name);
          return (
            <div key={`m-${region.name}`} className="flex flex-col gap-sm">
              {/* Region — underlined heading */}
              <button
                type="button"
                onClick={() => toggleSet(setExpandedRegions, region.name)}
                className="w-full text-left pb-xs border-b border-greyscale-border"
              >
                <div className="flex items-center justify-between gap-sm">
                  <span className="text-base font-semibold text-greyscale-text-title truncate min-w-0 flex-1">{region.name}</span>
                  <div className="flex items-center gap-sm shrink-0">
                    <AdherenceCell value={region.rosterAdherence} />
                    {regionExpanded ? (
                      <ChevronUp size={16} className="text-greyscale-text-caption" />
                    ) : (
                      <ChevronDown size={16} className="text-greyscale-text-caption" />
                    )}
                  </div>
                </div>
                <div className="mt-xs text-xs text-greyscale-text-caption">
                  {region.clockedIn}/{region.scheduled} in · {region.noShow} no-show · {region.late} late
                </div>
              </button>

              {regionExpanded && region.areas.map((area) => {
                const areaKey = `${region.name}::${area.name}`;
                const areaExpanded = expandedAreas.has(areaKey);
                return (
                  <div key={`m-${areaKey}`} className="flex flex-col gap-sm">
                    {/* Area — underlined sub-heading */}
                    <button
                      type="button"
                      onClick={() => toggleSet(setExpandedAreas, areaKey)}
                      className="w-full text-left pb-xs border-b border-greyscale-border"
                    >
                      <div className="flex items-center justify-between gap-sm">
                        <span className="text-sm font-medium text-greyscale-text-body truncate min-w-0 flex-1">{area.name}</span>
                        <div className="flex items-center gap-sm shrink-0">
                          <AdherenceCell value={area.rosterAdherence} />
                          {areaExpanded ? (
                            <ChevronUp size={14} className="text-greyscale-text-caption" />
                          ) : (
                            <ChevronDown size={14} className="text-greyscale-text-caption" />
                          )}
                        </div>
                      </div>
                      <div className="mt-xs text-xs text-greyscale-text-caption">
                        {area.clockedIn}/{area.scheduled} in · {area.noShow} no-show · {area.late} late
                      </div>
                    </button>

                    {/* Sites — tappable cards */}
                    {areaExpanded && (
                      <div className="flex flex-col gap-sm">
                        {area.sites.map((site, idx) => (
                          <button
                            key={`m-${areaKey}-${idx}`}
                            type="button"
                            onClick={() => site.detail && setSelectedSite(site.detail)}
                            className="w-full rounded-lg border border-greyscale-border bg-white p-sm text-left shadow-sm"
                          >
                            <div className="flex items-center justify-between gap-sm">
                              <span className="text-sm font-semibold text-greyscale-text-title truncate min-w-0 flex-1">{site.name}</span>
                              <AdherenceCell value={site.rosterAdherence} />
                            </div>
                            <div className="flex flex-wrap items-center gap-x-md gap-y-xxxs mt-xs text-xs text-greyscale-text-caption">
                              <span>{site.clockedIn}/{site.scheduled} in</span>
                              <span>{site.noShow} no-show</span>
                              <span>{site.late} late</span>
                            </div>
                            <div className="flex items-center justify-end gap-xxs mt-sm text-xs font-semibold text-primary-brand-primary">
                              Open site
                              <ChevronRight size={14} />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Table — desktop only */}
      <div className="hidden sm:block">
      <div className="rounded-lg border border-greyscale-border overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-[2fr_80px_100px_100px_100px_80px_120px_80px] bg-neutral-50 px-md py-sm border-b border-greyscale-border">
          <span className="text-xs font-semibold text-greyscale-text-caption uppercase tracking-wide">Region/Site</span>
          <span className="text-xs font-semibold text-greyscale-text-caption uppercase tracking-wide">Sites</span>
          <span className="text-xs font-semibold text-greyscale-text-caption uppercase tracking-wide">Scheduled</span>
          <span className="text-xs font-semibold text-greyscale-text-caption uppercase tracking-wide">Clocked in</span>
          <span className="text-xs font-semibold text-greyscale-text-caption uppercase tracking-wide">No Show</span>
          <span className="text-xs font-semibold text-greyscale-text-caption uppercase tracking-wide">Late</span>
          <span className="text-xs font-semibold text-greyscale-text-caption uppercase tracking-wide">Roster Adherence</span>
          <span className="text-xs font-semibold text-greyscale-text-caption uppercase tracking-wide">Action</span>
        </div>

        {/* Table body */}
        {regions.map((region) => {
          const regionExpanded = expandedRegions.has(region.name);

          // Traffic light counts from areas
          const greenCount = region.areas.filter((a) => a.rosterAdherence >= 94).length;
          const amberCount = region.areas.filter((a) => a.rosterAdherence >= 90 && a.rosterAdherence < 94).length;
          const redCount = region.areas.filter((a) => a.rosterAdherence < 90).length;

          return (
            <div key={region.name} className="[&:last-child>button]:border-b-0">
              {/* Level 1: Region row */}
              <button
                type="button"
                onClick={() => toggleSet(setExpandedRegions, region.name)}
                className="w-full grid grid-cols-[2fr_80px_100px_100px_100px_80px_120px_80px] px-md py-sm border-b border-greyscale-border hover:bg-neutral-50 transition-colors items-center text-left"
              >
                <span className="flex items-center gap-sm">
                  {regionExpanded ? (
                    <ChevronUp size={16} className="text-greyscale-text-caption" />
                  ) : (
                    <ChevronDown size={16} className="text-greyscale-text-caption" />
                  )}
                  <span className="text-sm font-semibold text-greyscale-text-title">{region.name}</span>
                  {region.areas.length > 0 && (
                    <span className="flex items-center gap-xs ml-sm">
                      <span className="flex items-center gap-xxxs text-xs font-medium">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span className="text-emerald-600">{region.areas.flatMap((a) => a.sites).filter((s) => s.rosterAdherence >= 94).length}</span>
                      </span>
                      <span className="flex items-center gap-xxxs text-xs font-medium">
                        <span className="w-2 h-2 rounded-full bg-amber-400" />
                        <span className="text-amber-500">{region.areas.flatMap((a) => a.sites).filter((s) => s.rosterAdherence >= 90 && s.rosterAdherence < 94).length}</span>
                      </span>
                      <span className="flex items-center gap-xxxs text-xs font-medium">
                        <span className="w-2 h-2 rounded-full bg-red-500" />
                        <span className="text-red-500">{region.areas.flatMap((a) => a.sites).filter((s) => s.rosterAdherence < 90).length}</span>
                      </span>
                    </span>
                  )}
                </span>
                <span className="text-sm text-greyscale-text-body">{region.totalSites}</span>
                <span className="text-sm font-semibold text-greyscale-text-title">{region.scheduled}</span>
                <span className="text-sm font-semibold text-greyscale-text-title">{region.clockedIn}</span>
                <span className="text-sm text-greyscale-text-body">{region.noShow}</span>
                <span className="text-sm text-greyscale-text-body">{region.late}</span>
                <AdherenceCell value={region.rosterAdherence} />
                <span />
              </button>

              {/* Level 2: Area rows (expanded from region) */}
              {regionExpanded && region.areas.map((area) => {
                const areaKey = `${region.name}::${area.name}`;
                const areaExpanded = expandedAreas.has(areaKey);

                return (
                  <div key={areaKey}>
                    {/* Area row */}
                    <button
                      type="button"
                      onClick={() => toggleSet(setExpandedAreas, areaKey)}
                      className="w-full grid grid-cols-[2fr_80px_100px_100px_100px_80px_120px_80px] px-md py-sm border-b border-greyscale-border hover:bg-neutral-50 transition-colors items-center text-left"
                    >
                      <span className="flex items-center gap-sm pl-lg">
                        {areaExpanded ? (
                          <ChevronUp size={14} className="text-greyscale-text-caption" />
                        ) : (
                          <ChevronDown size={14} className="text-greyscale-text-caption" />
                        )}
                        <span className="text-sm font-medium text-greyscale-text-title">{area.name}</span>
                        <span className="flex items-center gap-xs ml-sm">
                          <span className="flex items-center gap-xxxs text-xs font-medium">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-emerald-600">{area.sites.filter((s) => s.rosterAdherence >= 94).length}</span>
                          </span>
                          <span className="flex items-center gap-xxxs text-xs font-medium">
                            <span className="w-2 h-2 rounded-full bg-amber-400" />
                            <span className="text-amber-500">{area.sites.filter((s) => s.rosterAdherence >= 90 && s.rosterAdherence < 94).length}</span>
                          </span>
                          <span className="flex items-center gap-xxxs text-xs font-medium">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            <span className="text-red-500">{area.sites.filter((s) => s.rosterAdherence < 90).length}</span>
                          </span>
                        </span>
                      </span>
                      <span className="text-sm text-greyscale-text-body">{area.sites.length}</span>
                      <span className="text-sm text-greyscale-text-body">{area.scheduled}</span>
                      <span className="text-sm text-greyscale-text-body">{area.clockedIn}</span>
                      <span className="text-sm text-greyscale-text-body">{area.noShow}</span>
                      <span className="text-sm text-greyscale-text-body">{area.late}</span>
                      <AdherenceCell value={area.rosterAdherence} />
                      <span />
                    </button>

                    {/* Level 3: Site rows (expanded from area) */}
                    {areaExpanded && area.sites.map((site, idx) => (
                      <div
                        key={`${areaKey}-${idx}`}
                        className="grid grid-cols-[2fr_80px_100px_100px_100px_80px_120px_80px] px-md py-sm border-b border-greyscale-border bg-white items-center"
                      >
                        <span className="pl-[72px]">
                          <span className="text-sm text-greyscale-text-body">{site.name}</span>
                        </span>
                        <span />
                        <span className="text-sm text-greyscale-text-body">{site.scheduled}</span>
                        <span className="text-sm text-greyscale-text-body">{site.clockedIn}</span>
                        <span className="text-sm text-greyscale-text-body">{site.noShow}</span>
                        <span className="text-sm text-greyscale-text-body">{site.late}</span>
                        <AdherenceCell value={site.rosterAdherence} />
                        <span className="flex items-center justify-center gap-xxxs">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 sm:h-8 sm:w-8"
                            onClick={() => site.detail && setSelectedSite(site.detail)}
                            aria-label={`View ${site.name}`}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 sm:h-8 sm:w-8"
                            onClick={() => onNavigate?.("site-configuration-detail")}
                            aria-label={`Configure ${site.name}`}
                          >
                            <SettingsIcon size={16} />
                          </Button>
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      </div>

    </div>
  );
}
